import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Printer } from 'lucide-react';
import { financeApi } from '../../api/finance';
import { genericDocumentApi } from '../../api/generic-documents';
import { DataTable } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { IconActionButton } from '../../components/IconActionButton';
import { WorkflowStepper } from '../../components/WorkflowStepper';
import { CommentsThread } from '../../components/CommentsThread';
import { DocumentsSection } from '../../components/DocumentsSection';
import { RecordHeader } from '../../components/RecordHeader';
import { WorkflowTrackingTab } from '../../components/WorkflowTrackingTab';
import { JOURNAL_ENTRY_STAGES } from '../../config/workflow-stages';
import { WorkspaceTabs } from '../../components/WorkspaceTabs';
import { usePermission } from '../../auth/AuthContext';
import { formatDate } from '../../utils/formatDate';

const JOURNAL_ENTRY_STEPS = [
  { key: 'draft', label: 'Draft' },
  { key: 'submitted', label: 'Submitted' },
  { key: 'under_review', label: 'Under Review' },
  { key: 'approved', label: 'Approved' },
  { key: 'posted', label: 'Posted' },
];

export function JournalEntryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const journalId = Number(id);
  const canManage = usePermission('finance.journal.create');
  const detailQuery = useQuery({ queryKey: ['finance-journal-detail', journalId], queryFn: () => financeApi.getJournalEntry(journalId) });
  const docsQuery = useQuery({ queryKey: ['journal-entry-documents', journalId], queryFn: () => genericDocumentApi.listFor('journal_entry', journalId) });

  if (!detailQuery.data) return <div className="page"><div className="loading-state"><span className="loading-spinner" />Loading…</div></div>;
  const { data: entry, lines } = detailQuery.data;

  const totalDebit = lines.reduce((sum, l) => sum + Number(l.debit_amount), 0);
  const totalCredit = lines.reduce((sum, l) => sum + Number(l.credit_amount), 0);

  return (
    <div className="page">
      <Link to="/finance/journal-entries">&larr; Back to Journal Entries</Link>
      <RecordHeader
        title={entry.journal_reference}
        entityType="journal_entry"
        entityId={journalId}
        fields={[
          { label: 'Entry date', value: formatDate(entry.entry_date) },
          { label: 'Description', value: entry.description },
          { label: 'Status', value: <StatusBadge status={entry.status} /> },
        ]}
        actions={<div className="icon-action-group"><IconActionButton icon={Printer} label="Print" href={financeApi.journalEntryPdfUrl(journalId)} /></div>}
        readOnly={!canManage}
      />

      <WorkspaceTabs
        tabs={[
          {
            key: 'status',
            label: 'Status',
            content: (
              <>
                <section className="card">
                  <WorkflowStepper
                    steps={JOURNAL_ENTRY_STEPS}
                    currentStatus={entry.status}
                    detourLabel={entry.status === 'returned' ? 'Returned for Redraft' : entry.status === 'rejected' ? 'Rejected' : entry.status === 'reversed' ? 'Reversed' : undefined}
                    reachedIndex={entry.status === 'returned' ? (entry.reviewed_at ? 2 : 1) : entry.status === 'rejected' ? 2 : entry.status === 'reversed' ? 4 : undefined}
                  />
                </section>
                {entry.reversal_of_journal_id && (
                  <p className="muted">This is a reversal of <Link to={`/finance/journal-entries/${entry.reversal_of_journal_id}`}>journal #{entry.reversal_of_journal_id}</Link>.</p>
                )}
                {entry.reversed_by_journal_id && (
                  <p className="muted">This journal was reversed by <Link to={`/finance/journal-entries/${entry.reversed_by_journal_id}`}>journal #{entry.reversed_by_journal_id}</Link>. Reason: {entry.reversal_reason}</p>
                )}
                <CommentsThread entityType="journal_entry" entityId={journalId} section="status" heading="Comments for Status" />
              </>
            ),
          },
          {
            key: 'lines',
            label: 'Lines',
            content: (
              <>
                <section className="card">
                  <h2>Lines</h2>
                  <DataTable
                    rows={lines}
                    rowKey={(l) => l.id}
                    emptyMessage="No lines."
                    columns={[
                      { header: 'Account', render: (l) => `${l.account_code} - ${l.account_name}` },
                      { header: 'Description', render: (l) => l.description ?? '-' },
                      { header: 'Debit', render: (l) => Number(l.debit_amount) > 0 ? Number(l.debit_amount).toLocaleString() : '-' },
                      { header: 'Credit', render: (l) => Number(l.credit_amount) > 0 ? Number(l.credit_amount).toLocaleString() : '-' },
                    ]}
                  />
                  <p><strong>Total: Debit {totalDebit.toLocaleString()} · Credit {totalCredit.toLocaleString()}</strong> {totalDebit === totalCredit ? '(Balanced)' : '(Not Balanced)'}</p>
                </section>
                {entry.return_comment && <p className="muted">Return comment: {entry.return_comment}</p>}
                {entry.decision_comment && <p className="muted">Decision comment: {entry.decision_comment}</p>}
                <CommentsThread entityType="journal_entry" entityId={journalId} section="lines" heading="Comments for Lines" />
              </>
            ),
          },
          {
            key: 'documents',
            label: 'Documents',
            content: (
              <DocumentsSection
                documents={docsQuery.data?.data ?? []}
                downloadUrl={(documentId) => genericDocumentApi.downloadUrl('journal_entry', documentId)}
                upload={(input) => genericDocumentApi.uploadFor('journal_entry', journalId, input)}
                onDelete={(documentId) => genericDocumentApi.deleteFor('journal_entry', documentId)}
                queryKeyToInvalidate={['journal-entry-documents', journalId]}
                hrCategories={false}
              />
            ),
          },
          {
            key: 'tracking',
            label: 'Tracking',
            content: <WorkflowTrackingTab entityType="journal_entry" entityId={journalId} actionStageMap={JOURNAL_ENTRY_STAGES} />,
          },
        ]}
      />
    </div>
  );
}
